import { Box, alpha, useTheme } from "@mui/material";
import React, { useRef, useState } from "react";

import { FileItem } from "@/common/fileItem";

import Image from "../Image";
import ZoomableBlock from "../ZoomableBlock";

interface ImageComparisonSliderProps {
  leftImage: FileItem;
  rightImage: FileItem;
  height?: number | string;
  resetSliderPositionKey?: number;
}

const ImageComparisonSlider: React.FC<ImageComparisonSliderProps> = ({
  leftImage,
  rightImage,
  height: _height = "100%",
  resetSliderPositionKey,
}) => {
  const theme = useTheme();
  const [sliderPos, setSliderPos] = useState(50); // percent of container width
  const containerRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);

  React.useEffect(() => {
    setSliderPos(50); // reset to center on image change
  }, [leftImage, rightImage]);

  React.useEffect(() => {
    if (typeof resetSliderPositionKey !== "undefined") {
      setSliderPos(50); // reset to center when key changes
    }
  }, [resetSliderPositionKey]);

  // Drag logic
  const onDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    setDragging(true);
    e.preventDefault();
  };

  const onDrag = (e: MouseEvent | TouchEvent) => {
    if (!containerRef.current) return;

    let clientX = 0;
    // Check if it's a touch event by looking for the touches property
    if ("touches" in e && e.touches) {
      if (e.touches.length > 0) {
        clientX = e.touches[0].clientX;
      } else {
        return; // No touch points
      }
    } else {
      clientX = (e as MouseEvent).clientX;
    }

    const rect = containerRef.current.getBoundingClientRect();
    let percent = ((clientX - rect.left) / rect.width) * 100;
    percent = Math.max(0, Math.min(100, percent));
    setSliderPos(percent);
  };

  React.useEffect(() => {
    if (!dragging) return;

    const move = (e: MouseEvent | TouchEvent) => {
      e.preventDefault(); // Prevent scrolling
      onDrag(e);
    };

    const up = () => setDragging(false);

    document.addEventListener("mousemove", move);
    document.addEventListener("touchmove", move, { passive: false });
    document.addEventListener("mouseup", up);
    document.addEventListener("touchend", up);

    return () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("touchmove", move);
      document.removeEventListener("mouseup", up);
      document.removeEventListener("touchend", up);
    };
  }, [dragging, onDrag]);

  // Calculate pixel-based clip for left image based on container width
  let clipRightPx = 0;
  if (containerRef.current) {
    const containerW = containerRef.current.offsetWidth;
    clipRightPx = Math.max(0, containerW - (containerW * sliderPos) / 100);
  }

  if (!leftImage || !rightImage) {
    return null;
  }
  const leftSrc = leftImage.blob
    ? URL.createObjectURL(leftImage.blob)
    : leftImage.file && leftImage.file.url
      ? leftImage.file.url
      : (leftImage as any).url;
  const rightSrc = rightImage.blob
    ? URL.createObjectURL(rightImage.blob)
    : rightImage.file && rightImage.file.url
      ? rightImage.file.url
      : (rightImage as any).url;

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        maxWidth: "100%",
        height: _height,
        overflow: "hidden",
        borderRadius: 2,
        boxShadow: 2,
        background: theme.palette.background.paper,
        touchAction: "none",
        userSelect: dragging ? "none" : undefined,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "@media (max-width:600px)": {
          height: 220,
        },
      }}
    >
      {/* Right image (background) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          background: theme.palette.background.paper, // Use theme background for right image
        }}
      >
        <ZoomableBlock
          minScale={1}
          maxScale={4}
          initialScale={1}
          wheel={{ step: 0.1 }}
          doubleClick={{ disabled: true }}
          panning={{ velocityDisabled: true }}
        >
          <Image
            src={rightSrc}
            alt='Right image'
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              background: theme.palette.background.paper, // Use theme background inside image
            }}
          />
        </ZoomableBlock>
      </Box>
      {/* Left image (foreground, clipped with pixel-based clip-path) */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 2,
          pointerEvents: "auto",
          clipPath: containerRef.current
            ? `inset(0px ${clipRightPx}px 0px 0px)`
            : `inset(0px ${100 - sliderPos}% 0px 0px)`,
          WebkitClipPath: containerRef.current
            ? `inset(0px ${clipRightPx}px 0px 0px)`
            : `inset(0px ${100 - sliderPos}% 0px 0px)`,
          background: theme.palette.background.paper, // Use theme background for left image
        }}
      >
        <ZoomableBlock
          minScale={1}
          maxScale={4}
          initialScale={1}
          wheel={{ step: 0.1 }}
          doubleClick={{ disabled: true }}
          panning={{ velocityDisabled: true }}
        >
          <Image
            src={leftSrc}
            alt='Left image'
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              background: theme.palette.background.paper, // Use theme background inside image
            }}
          />
        </ZoomableBlock>
      </Box>
      {/* Slider handle */}
      <Box
        sx={{
          position: "absolute",
          left: `calc(${sliderPos}% - 7px)`, // Centered for 14px total width
          top: 0,
          width: 14,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 3,
          cursor: dragging ? "-webkit-grabbing" : "-webkit-grab",
          touchAction: "none",
          pointerEvents: "auto",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
        }}
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        {/* Vertical line (full height) */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: 0,
            transform: "translateX(-50%)",
            width: 2,
            height: "100%",
            // borderRadius: 2,
            background: alpha(theme.palette.background.paper, 0.8),
            boxShadow: 1,
          }}
        />
        {/* Central handle area */}
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "40%",
            transform: "translate(-50%, 0)",
            width: 8,
            height: "20%",
            borderRadius: 5,
            background: alpha(theme.palette.background.paper, 1),
            boxShadow: 2,
            border: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      </Box>
    </Box>
  );
};

export default ImageComparisonSlider;
